import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Printer, AlertTriangle, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { inputCls, btnSecondary } from "@/components/Modal";
import AllergenChips from "@/components/AllergenChips";
import { useAuth } from "@/context/AuthContext";

const SRC_LABEL = {
  reference_estimate: "Reference estimate",
  user_entered: "User entered",
  ai_estimate: "AI estimate",
};

export default function NutritionPanel({ formulaId, formula, onServingSaved }) {
  const { canWrite } = useAuth();
  const [editingServing, setEditingServing] = useState(false);
  const [serving, setServing] = useState(formula.serving_size_g ?? 20);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["nutrition", formulaId],
    queryFn: async () => (await api.get(`/formulas/${formulaId}/nutrition`)).data,
  });

  const saveServing = async () => {
    try {
      await api.patch(`/formulas/${formulaId}/serving-size`, { serving_size_g: Number(serving) });
      toast.success("Serving size updated");
      setEditingServing(false);
      refetch();
      onServingSaved?.();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (isLoading || !data) return <div className="text-sm text-[#8C7A6B]" data-testid="nutrition-loading">Computing nutrition…</div>;

  const { per_100g: per100, per_serve: perServe, rda_percent: rda, nutrient_order: order, labels } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="nutrition-tab">
      <div className="lg:col-span-2">
        <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden print-area" data-testid="nutrition-panel">
          <div className="px-6 py-4 border-b-2 border-[#2A1810]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-bold text-[#1C100B]">Nutritional Information</h2>
                <p className="text-xs text-[#8C7A6B] mt-1">
                  {data.formula_name} · per 100 g and per serve of {data.serving_size_g} g
                </p>
              </div>
              <button
                onClick={() => window.print()}
                className={`${btnSecondary} !py-2 no-print`}
                data-testid="print-nutrition-button"
              >
                <Printer size={14} className="inline mr-1.5 -mt-0.5" /> Print
              </button>
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">Nutrient</th>
                <th className="px-4 py-3 text-right font-semibold">Per 100 g</th>
                <th className="px-4 py-3 text-right font-semibold">Per serve ({data.serving_size_g} g)</th>
                <th className="px-6 py-3 text-right font-semibold">% RDA per serve*</th>
              </tr>
            </thead>
            <tbody>
              {order.map((key) => {
                const indented = labels[key].startsWith("—");
                return (
                  <tr key={key} className="border-b border-[#F4EFEA] last:border-0" data-testid={`nutrient-row-${key}`}>
                    <td className={`px-6 py-2.5 ${indented ? "pl-10 text-[#5C4A3E]" : "font-medium text-[#2A1810]"}`}>
                      {labels[key].replace("— ", "")}
                    </td>
                    <td className="px-4 py-2.5 text-right text-[#2A1810]">{per100[key]}</td>
                    <td className="px-4 py-2.5 text-right text-[#2A1810]">{perServe[key]}</td>
                    <td className="px-6 py-2.5 text-right text-[#8C7A6B]">{rda[key]}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="px-6 py-5 border-t border-[#E8DFD1] bg-[#FDFBF7] space-y-3 text-xs leading-relaxed">
            <div data-testid="nutrition-allergen-declaration">
              <span className="font-bold uppercase tracking-wider text-[#2A1810]">Allergen declaration: </span>
              {data.allergens.length ? (
                <span className="text-[#B91C1C] font-semibold">
                  Contains {data.allergens.map((a) => a.replace(/_/g, " ")).join(", ")}.
                </span>
              ) : (
                <span className="text-[#5C4A3E]">No declared allergens from the ingredient library.</span>
              )}
              <span className="text-[#8C7A6B]"> May contain traces from shared processing lines.</span>
            </div>
            {data.contains_added_sugars && (
              <div className="text-[#92400E] font-semibold" data-testid="added-sugars-note">
                Contains added sugars.
              </div>
            )}
            <div className="text-[#8C7A6B]">
              *% RDA based on a 2,000 kcal reference diet (FSSAI Schedule V). Values are calculated from supplier
              ingredient data and must be confirmed by lab analysis before label printing.
            </div>
            <div className="text-[#8C7A6B]" data-testid="nutrition-not-for-labeling">
              Estimated values for demonstration — not verified against a certified composition database. Not for
              labeling use.
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="serving-size-card">
          <h3 className="font-display text-base font-semibold text-[#1C100B] mb-3">Serving Size</h3>
          {editingServing ? (
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                max={1000}
                step="0.5"
                value={serving}
                onChange={(e) => setServing(e.target.value)}
                className={inputCls}
                data-testid="serving-size-input"
              />
              <button
                onClick={saveServing}
                className="bg-[#2A1810] hover:bg-[#3D2314] text-[#FDFBF7] px-3 rounded-lg"
                data-testid="serving-size-save-button"
              >
                <Check size={15} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-[#2A1810]" data-testid="serving-size-value">{data.serving_size_g} g</span>
              {canWrite && (
                <button onClick={() => setEditingServing(true)} className="p-2 rounded hover:bg-[#FAF6F0] text-[#5C4A3E]" data-testid="edit-serving-size-button">
                  <Pencil size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="nutrition-coverage-card">
          <h3 className="font-display text-base font-semibold text-[#1C100B] mb-3">Data Coverage</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-[#8C7A6B]">Lines with nutrition data</span>
              <span className="font-bold text-[#2A1810]" data-testid="coverage-percentage">{data.coverage.covered_percentage}%</span>
            </div>
            {!data.composition_ok && (
              <div className="flex items-start gap-2 bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-3 py-2.5 text-xs text-[#92400E]" data-testid="nutrition-composition-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                Ingredient lines sum to {data.percentage_sum}%, so these values are not a full 100 g basis. Fix the
                composition before using this panel on a label.
              </div>
            )}
            {data.coverage.missing_ingredients.length > 0 && (
              <div className="text-xs text-[#B91C1C] leading-relaxed" data-testid="nutrition-missing-list">
                Missing nutrition data: {data.coverage.missing_ingredients.join(", ")}. Add per-100 g values on the
                Ingredients page.
              </div>
            )}
            {data.coverage.complete && data.composition_ok && (
              <div className="text-xs text-[#065F46]" data-testid="nutrition-complete">
                Every ingredient line has nutrition data and the composition totals 100%.
              </div>
            )}
          </div>
          <div className="mt-4 pt-3 border-t border-[#F4EFEA]">
            <AllergenChips allergens={data.allergens} empty="no declared allergens" />
          </div>
          {data.ingredient_sources?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#F4EFEA]" data-testid="nutrition-sources">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#8C7A6B] mb-2">Data source per ingredient</h4>
              <ul className="space-y-1">
                {data.ingredient_sources.map((s, i) => (
                  <li key={`${s.name}-${i}`} className="flex items-center justify-between gap-2 text-xs" data-testid={`ingredient-source-${i}`}>
                    <span className="text-[#5C4A3E] truncate pr-2">{s.name}</span>
                    <span className="text-[#8C7A6B] shrink-0">{SRC_LABEL[s.nutrition_source] || "User entered"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
